import { useEffect, useState } from 'react';

const SCRIPT_ID = 'google-maps-script';

const resolveApiKey = providedKey => {
  if (providedKey) return providedKey;
  const envKey =
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
    import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
  if (!envKey) {
    // Production'da console.error kaldırılacak, ama development'ta bilgi verelim
    if (import.meta.env.DEV) {
      console.error(
        'Google Maps API key yok. Lütfen VITE_GOOGLE_MAPS_API_KEY değişkenini .env dosyanıza ekleyin.',
      );
    }
    return null;
  }
  return envKey;
};

export function useGoogleMapsScript({
  apiKey,
  libraries = ['places'],
  language = 'en',
} = {}) {
  const resolvedApiKey = resolveApiKey(apiKey);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ready) return;
    if (!resolvedApiKey) {
      setError('Missing Google Maps API key');
      return;
    }

    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      const checkExistingReady = () => {
        if (
          typeof window !== 'undefined' &&
          window.google &&
          window.google.maps &&
          window.google.maps.places &&
          window.google.maps.places.AutocompleteService
        ) {
          setReady(true);
        } else {
          existingScript.addEventListener('load', () => {
            const checkReady = () => {
              if (
                typeof window !== 'undefined' &&
                window.google &&
                window.google.maps &&
                window.google.maps.places &&
                window.google.maps.places.AutocompleteService
              ) {
                setReady(true);
              } else {
                setTimeout(checkReady, 100);
              }
            };
            checkReady();
          }, { once: true });
        }
      };
      checkExistingReady();
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.loading = 'async'; // Google'ın önerdiği şekilde
    const languageParam = language ? `&language=${language}` : '';
    // Key'i URL'de kullanmak zorundayız, ama warning'i azaltmak için callback kullanabiliriz
    script.src = `https://maps.googleapis.com/maps/api/js?key=${resolvedApiKey}&libraries=${libraries.join(
      ',',
    )}${languageParam}&loading=async`;
    script.onload = () => {
      // Script yüklendiğinde, API'nin tamamen hazır olduğundan emin ol
      const checkReady = () => {
        if (
          typeof window !== 'undefined' &&
          window.google &&
          window.google.maps &&
          window.google.maps.places &&
          window.google.maps.places.AutocompleteService
        ) {
          setReady(true);
        } else {
          // Henüz hazır değilse, kısa bir süre sonra tekrar kontrol et
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    };
    script.onerror = () => setError('Failed to load Google Maps script');

    document.head.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, [resolvedApiKey, libraries, language, ready]);

  return { ready, error };
}

